export enum StudioTemplateTypes {
  /** Multiple swimlanes */
  MODULE = "module",
  SWIMLANE = "stepgroups",
  STEP = "steps",
  FIELD = "fields",
  /** Basially a step with integration fields */
  CONNECTOR = "connector",
  MULTI_PROCESS = "multi-process"
}

/**
 * This is the updated field types. FIELD_TYPES (Notice the Plural form)
 * that's exported from `@certa/types` is not up-to-date. But it's being
 * used in the core platform, so didn't change it.
 * But ideally for all newer implementations we should stick to using these
 * and keep these updated.
 *
 * If a new field type is added, it should be added here, so it raises
 * proper errors wherever that field type is not implemented.
 */

export enum FieldType {
  // Simple field types
  "CHAR" = "char_field",
  "ARRAY" = "array",
  "BOOLEAN" = "bool",
  "BUSINESS_UNIT" = "business_unit",
  "CASCADER" = "cascader",
  "CHECKBOX" = "checkbox",
  "CURRENCY" = "currency",
  "NAIVE_DATE" = "naive_date",
  "DATE" = "date",
  "DOWNLOAD_ATTACHMENT" = "attachment",
  "DROPDOWN_SINGLE" = "single_select",
  "DROPDOWN_MULTI" = "multi_select",
  "DYNAMIC_GROUP_JSON" = "dynamic_group_json",
  "EMAIL" = "email",
  "EMBED_VIDEO" = "iframe",
  "GROUPED_CHECKBOX" = "grouped_checkbox",
  "INFO_DESCRIPTION_BOX" = "alert",
  "JSON" = "json",
  "LC_MESSAGE" = "lc_message",
  "DECIMAL" = "decimal",
  "INTEGER" = "integer",
  "PARAGRAPH" = "paragraph",
  "PDF_LINK" = "s3_object",
  "PHONE" = "phone",
  "PROCESS_LINKING_ALPHA" = "linked_process_alpha",
  "RADIO" = "radio",
  "REGION" = "region",
  "RICH_TEXT_EDITOR" = "html",
  "SECTION_BREAK" = "section_break",
  "SLIDER" = "slider",
  "SUPPLIER_CREATION" = "user_workflow",
  "SYSTEM" = "system",
  "TABLE" = "table",
  "FREE_TEXT" = "text",
  "FILE" = "file",
  "MULTI_FILE" = "multi_file",
  "URL" = "url",
  "VISUALIZATION" = "visualization",
  "WHITESPACE" = "break",
  "WIZARD_SECTION" = "wizard_section",
  "WORD_DOCUMENT" = "word_document",
  "COUNTRY_SINGLE_SELECT" = "country_single_select",
  "COUNTRY_MULTI_SELECT" = "country_multi_select",

  "POSTAL_CODE" = "postal_code",
  "TAX_ID" = "tax_id",
  "BANK_ACCOUNT_NUMBER" = "bank_account_number",
  "BANK_ROUTING_NUMBER" = "bank_routing_number",
  "SCREENING_UPDATE_EVENTS" = "screening_update_events",

  // Following field types were missed from the FIELD_TYPES
  "EMBEDDED_IN_APP_REPORT" = "embedded_in_app_report",
  "RELATIVE_DATE" = "relative_date",
  "DIVIDER" = "divider",
  "SCORM" = "scorm",
  "INTEGRATION" = "integration",
  "RATING" = "rating",
  "DNB_DIRECT_PLUS_APPLIED_CONTROL" = "dnb_direct_plus_applied_control",
  "DNB_DIRECT_PLUS_CNO" = "dnb_direct_plus_cno",
  "PAYPAL" = "paypal",
  "SAYARI_UBO_GRAPH" = "sayari_ubo_graph",
  "CIAL_CERTIFICATE" = "cial_certificate",

  // Data Object Field Types
  "DATA_OBJECT" = "data_object", // Table field
  "DATA_OBJECT_MAPPING" = "data_object_mapping", // Field mapping

  // Campaign Field Types
  "QUESTIONNAIRE_LIST" = "questionnaire_list", // Questionnaire selection dropdown
  "DATA_OBJECTS_SURVEY_TABLE" = "data_objects_survey_table", // Survey recipient selection table
  "DATA_OBJECT_SIBLING" = "data_object_sibling",
  "DATA_OBJECTS_TABLE" = "data_objects_table",

  "INVITE_THIRD_PARTY_USER_BUTTON" = "invite_third_party_user_button",
  "OWNER" = "owner",

  // Integrations
  "DNB_DUNS_SEARCH" = "dnb_duns_search",
  "DNB_DUNS_SEARCH_DIRECT_PLUS" = "dnb_duns_search_direct_plus",
  "DNB_COMPANY_PROFILE" = "dnb_company_profile",
  "DNB_RISK_SCORE" = "dnb_risk_score",
  "DNB_UBO" = "dnb_ubo",
  "DNB_UBO1" = "dnb_ubo1",
  "DNB_UBO_GRAPH" = "dnb_ubo_graph",
  "DNB_DIRECTORS" = "dnb_directors",
  "DNB_RDC" = "dnb_rdc",
  "DNB_RDC1" = "dnb_rdc1",
  "DNB_RDC_ALERTS" = "dnb_rdc_alerts",
  "DNB_CMP_ENT_VW" = "dnb_cmp_ent_vw",
  "DNB_CMPCVF" = "dnb_cmpcvf",
  "DNB_FAMILYTREE" = "dnb_familytree",
  "DNB_FAMILYTREE_BRANCH" = "dnb_familytree_branch",
  "DNB_DATA_READER" = "dnb_data_reader",
  "DNB_FINANCIALS" = "dnb_financials",
  "DNB_LITIGATION" = "dnb_litigation",
  "IBAN_SEARCH" = "iban_search",
  "EU_VAT_CHECK" = "eu_vat_check",
  "US_TIN_CHECK" = "us_tin_check",
  "CHARITY_CHECK" = "charity_check",
  "WHOIS_SEARCH" = "whois_search",
  "CLEARBIT_SEARCH" = "clearbit_search",
  "CSV_SEARCH" = "csv_search",
  "LN_SEARCH" = "ln_search",
  "SALESFORCE" = "salesforce",
  "GOOGLE_SEARCH" = "google_search",
  "TRANSLATION" = "translation",
  "TRANSLITERATION" = "transliteration",
  "THOMSON_REUTERS_GROUP" = "thomson_reuters_group",
  "THOMSON_REUTERS_CASE" = "thomson_reuters_case",
  "THOMSON_REUTERS_SCREEN" = "thomson_reuters_screen",
  "THOMSON_REUTERS_SCREENRESULT" = "thomson_reuters_screenresult",
  "THOMSON_REUTERS_WCPROFILE" = "thomson_reuters_wcprofile",
  "THOMSON_REUTERS_SCREEN_SYNC" = "thomson_reuters_screen_sync",
  "THOMSON_REUTERS_CASESYSTEMID" = "thomson_reuters_casesystemid",
  "THOMSON_REUTERS_RESOLVE_RESULT" = "thomson_reuters_resolve_result",
  "HERMES" = "hermes",
  "HERMES_2" = "hermes_2",
  "HERMES_DNB_DIVERSITY_2" = "hermes_dnb_diversity_2",
  "CHILD_WORKFLOW" = "child_workflow",
  "DUPLICATE_CHECK" = "duplicate_check",
  "APPBUILDER_DUPLICATE_CHECK" = "appbuilder_duplicate_check",
  "RECORDS_DUPLICATE_CHECK" = "records_duplicate_check",
  "CSI" = "csi",
  "BOEING_SCREENING_STATUS" = "boeing_screening_status",
  "BOEING_ADJUDICATION_STATUS" = "boeing_adjudication_status",
  "DNB_GBO" = "dnb_gbo",
  "RDC_EVENT_DETAILS" = "rdc_event_details",
  "GREYLIST_CHECK" = "greylist_check",
  "PM_GREYLIST_CHECK" = "pm_greylist_check",
  "RDC_ALERT_METADATA" = "rdc_alert_metadata",
  "DNB_INITIATE_INVESTIGATION" = "dnb_initiate_investigation",
  "DNB_INVESTIGATION_STATUS" = "dnb_investigation_status",
  "OCR" = "ocr",
  "DOCUSIGN" = "docusign",
  "KRYPTON_AI_PUSH" = "krypton_ai_push",
  "ADOBE_SIGN" = "adobe_sign",
  "AMBER_ROAD" = "amber_road",
  "CREATE_REUSABLE_COMPONENT" = "create_reusable_component",
  "CODE_MIGRATION" = "code_migration",
  "PLATFORM_SETTINGS" = "platform_settings",
  "WORKFLOW_STATUS" = "workflow_status",
  "INTEGRATION_WORD_DOCUMENT_DOWNLOAD" = "word_document_download",
  "DOCUSIGN_DOWNLOAD" = "docusign_document",
  "RDC_STOP_INQ" = "rdc_stop_inq",
  "JUXTAPOSE" = "juxtapose",
  "IMAGE" = "image",
  "WORKDAY" = "workday",
  "HERMES_DNB_DIVERSITY" = "hermes_dnb_diversity",
  "COMPLY_EXCHANGE" = "comply_exchange",
  "WORKFLOW_LOGO" = "workflow_logo",
  "PACKAGE_DEPLOY" = "package_deploy",
  "CODEDEPLOY_TO_ENV" = "codedeploy_to_env",
  "PUBLISHING" = "publishing",
  "E_SIGNATURE" = "e_signature",
  "PDF_VIEWER" = "pdf_viewer",
  "GOOGLE_ADDRESS_SEARCH" = "google_address_search",
  "SCREENING" = "screening",
  "RISK_AI" = "risk_ai",
  "DATA_OBJECTS_SELECTION_TABLE" = "data_objects_selection_table"
}

export type FieldTypesMetaData = Exclude<
  FieldType,
  | "embedded_in_app_report"
  | "relative_date"
  | "divider"
  | "scorm"
  | "integration"
  | "rating"
  | "dnb_direct_plus_applied_control"
  | "dnb_direct_plus_cno"
  | "paypal"
  | "sayari_ubo_graph"
  | "cial_certificate"
  | "data_object"
  | "data_object_mapping"
  | "data_object_sibling"
  | "data_objects_table"
  | "data_objects_selection_table"
  | "invite_third_party_user_button"
  // Remove these after adding support in Studio
  | "country_single_select"
  | "country_multi_select"
  | "postal_code"
  | "tax_id"
  | "bank_account_number"
  | "bank_routing_number"
  | "risk_ai"
>;

export const BASIC_FIELD_TYPES: FieldTypesMetaData[] = [
  FieldType.CHAR,
  FieldType.ARRAY,
  FieldType.BOOLEAN,
  FieldType.BUSINESS_UNIT,
  FieldType.CASCADER,
  FieldType.CHECKBOX,
  FieldType.CURRENCY,
  FieldType.NAIVE_DATE,
  FieldType.DATE,
  FieldType.DOWNLOAD_ATTACHMENT,
  FieldType.DROPDOWN_SINGLE,
  FieldType.DROPDOWN_MULTI,
  FieldType.DYNAMIC_GROUP_JSON,
  FieldType.EMAIL,
  FieldType.EMBED_VIDEO,
  FieldType.GROUPED_CHECKBOX,
  FieldType.INFO_DESCRIPTION_BOX,
  FieldType.JSON,
  FieldType.LC_MESSAGE,
  FieldType.DECIMAL,
  FieldType.INTEGER,
  FieldType.PARAGRAPH,
  FieldType.PDF_LINK,
  FieldType.PHONE,
  FieldType.PROCESS_LINKING_ALPHA,
  FieldType.RADIO,
  FieldType.REGION,
  FieldType.RICH_TEXT_EDITOR,
  FieldType.SECTION_BREAK,
  FieldType.SLIDER,
  FieldType.SUPPLIER_CREATION,
  FieldType.SYSTEM,
  FieldType.TABLE,
  FieldType.FREE_TEXT,
  FieldType.FILE,
  FieldType.MULTI_FILE,
  FieldType.URL,
  FieldType.VISUALIZATION,
  FieldType.WHITESPACE,
  FieldType.WIZARD_SECTION,
  FieldType.WORD_DOCUMENT,
  FieldType.SCREENING,
  FieldType.SCREENING_UPDATE_EVENTS,
  FieldType.OWNER
];

export const INTEGRATION_FIELD_TYPES = [
  FieldType.DNB_DUNS_SEARCH,
  FieldType.DNB_DUNS_SEARCH_DIRECT_PLUS,
  FieldType.DNB_COMPANY_PROFILE,
  FieldType.DNB_RISK_SCORE,
  FieldType.DNB_UBO,
  FieldType.DNB_UBO1,
  FieldType.DNB_UBO_GRAPH,
  FieldType.DNB_DIRECTORS,
  FieldType.DNB_RDC,
  FieldType.DNB_RDC1,
  FieldType.DNB_RDC_ALERTS,
  FieldType.DNB_CMP_ENT_VW,
  FieldType.DNB_CMPCVF,
  FieldType.DNB_FAMILYTREE,
  FieldType.DNB_FAMILYTREE_BRANCH,
  FieldType.DNB_DATA_READER,
  FieldType.DNB_FINANCIALS,
  FieldType.DNB_LITIGATION,
  FieldType.IBAN_SEARCH,
  FieldType.EU_VAT_CHECK,
  FieldType.US_TIN_CHECK,
  FieldType.CHARITY_CHECK,
  FieldType.WHOIS_SEARCH,
  FieldType.CLEARBIT_SEARCH,
  FieldType.CSV_SEARCH,
  FieldType.LN_SEARCH,
  FieldType.SALESFORCE,
  FieldType.GOOGLE_SEARCH,
  FieldType.TRANSLATION,
  FieldType.TRANSLITERATION,
  FieldType.THOMSON_REUTERS_GROUP,
  FieldType.THOMSON_REUTERS_CASE,
  FieldType.THOMSON_REUTERS_SCREEN,
  FieldType.THOMSON_REUTERS_SCREENRESULT,
  FieldType.THOMSON_REUTERS_WCPROFILE,
  FieldType.THOMSON_REUTERS_SCREEN_SYNC,
  FieldType.THOMSON_REUTERS_CASESYSTEMID,
  FieldType.THOMSON_REUTERS_RESOLVE_RESULT,
  FieldType.HERMES,
  FieldType.HERMES_2,
  FieldType.HERMES_DNB_DIVERSITY_2,
  FieldType.CHILD_WORKFLOW,
  FieldType.DUPLICATE_CHECK,
  FieldType.APPBUILDER_DUPLICATE_CHECK,
  FieldType.CSI,
  FieldType.BOEING_SCREENING_STATUS,
  FieldType.BOEING_ADJUDICATION_STATUS,
  FieldType.DNB_GBO,
  FieldType.RDC_EVENT_DETAILS,
  FieldType.GREYLIST_CHECK,
  FieldType.PM_GREYLIST_CHECK,
  FieldType.RDC_ALERT_METADATA,
  FieldType.DNB_INITIATE_INVESTIGATION,
  FieldType.DNB_INVESTIGATION_STATUS,
  FieldType.OCR,
  FieldType.DOCUSIGN,
  FieldType.KRYPTON_AI_PUSH,
  FieldType.ADOBE_SIGN,
  FieldType.AMBER_ROAD,
  FieldType.CREATE_REUSABLE_COMPONENT,
  FieldType.CODE_MIGRATION,
  FieldType.PLATFORM_SETTINGS,
  FieldType.WORKFLOW_STATUS,
  FieldType.INTEGRATION_WORD_DOCUMENT_DOWNLOAD,
  FieldType.DOCUSIGN_DOWNLOAD,
  FieldType.RDC_STOP_INQ,
  FieldType.JUXTAPOSE,
  FieldType.IMAGE,
  FieldType.WORKDAY,
  FieldType.HERMES_DNB_DIVERSITY,
  FieldType.COMPLY_EXCHANGE,
  FieldType.WORKFLOW_LOGO,
  FieldType.PACKAGE_DEPLOY,
  FieldType.CODEDEPLOY_TO_ENV,
  FieldType.PUBLISHING,
  FieldType.E_SIGNATURE,
  FieldType.PDF_VIEWER,
  FieldType.GOOGLE_ADDRESS_SEARCH
];
